import {
    DetailsListLayoutMode,
    IColumn,
    IContextualMenuItem,
    Selection,
    SelectionMode
} from '@fluentui/react';
import { RootState } from 'app/store';
import CustomPanel from 'components/controls/CustomPanel';
import { EControlType } from 'components/ControlWrapper';
import { addUpdateGroupStructure } from 'global/Reducer/dataSlice';
import { copyPropertiesOnly } from 'global/Utility/TSFunctions';
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IGroupNumber as FAM_IGroupNumber,
    IPlan as FAM_IPlan,
    IStopLoss as FAM_IStopLoss
} from 'model/FlexibleAttributeModel';
import { IGroupConfigurationData } from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import {
    createGroupNumber,
    createGroupStructureColumns,
    DetailListExcelGenerationAsync,
    emitCustomEvent, friendlyValue, subscribeCustomEvent
} from "../../global/Application";

import GroupStructureGroupNumberEditor from './GroupStructureGroupNumberEditor';

import { RouterParams } from 'App';
import CustomDetailList from 'components/controls/CustomDetailList';
import { DefaultGridConfig } from 'components/controls/CustomDetailList/CustomDetailList';
import { ICustomDetailListConfiguration } from 'components/controls/CustomDetailList/ICustomDetailListConfiguration';
import { IGridItem } from 'components/controls/CustomDetailList/IGridItem';
import { useApplication } from 'global/Application/Context';
import { updateCustomDetailListConfiguration } from 'global/Reducer/statusSlice';
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type';
import { useHistory, useParams } from 'react-router';


type TGroupNumberGridItem = IGridItem<FAM_IGroupNumber>;

interface IProps extends TComponentConnectedProps {
    onPick?: (index: number) => void;
    snap?: IGroupConfigurationData;

    // if provided, filters display grid to only referenced for this plan
    currentPlan?: FAM_IPlan;

    // if provided, filters display grid to only referenced for this accounts receivable
    currentAR?: FAM_IAccountsReceivable;

    // if provided, filters display grid to only referenced for this accounts receivable
    currentStopLoss?: FAM_IStopLoss;

    disabled?: boolean;

    visibility?: IVisibilityScope
}


const GroupStructureGroupNumberView = (props: IProps) => {
    const history = useHistory();
    const application = useApplication();
    const params = useParams<RouterParams>();
    const dispatch = useDispatch();
    const [groupNumber, setGroupNumber] = useState<FAM_IGroupNumber | null>(null);

    const snap = props.snap || props.state.data.json;
    const [selectionCount, setSelectionCount] = useState(0);

    const storageId = `groupStructure.groupNumber`;

    const scopeGridConfig = () => {
        const u = props.state.status.gridConfigs[storageId];
        if (u)
            return u;

        const t = DefaultGridConfig();
        t.pageSize = 500; // #552445 - Group Numbers exception: paginate at 500 lines
        t.sort = [{ column: { key: 'groupNumber' }, isSortedDescending: false }];

        return t;
    }

    const [gridConfig, setGridConfig] = useState<ICustomDetailListConfiguration>(scopeGridConfig());
    const gridConfigRef = useRef(gridConfig);

    // check if it has changed since mounting
    if (gridConfig !== gridConfigRef.current) {
        // console.log("render:grid config updated", gridConfig);

        gridConfigRef.current = gridConfig;

        // snapshot for bind back
        dispatch(updateCustomDetailListConfiguration({ id: storageId, gridConfig: structuredClone(gridConfig) }));
    }


    useEffect(() => {
        const unsubscribe = subscribeCustomEvent({
            eventType: 'NewGroupConfiguration',
            handler: (event: any) => {
                setGroupNumber(event.detail);
            },
        });

        // Unmount - Cleanup
        return () => {
            unsubscribe();
        }
    }, [])

    const onSelect = (items: FAM_IGroupNumber[]) => {
        if (items) {
            setSelectedItems(items);

            if (items.length === 1) {
                // single items selected for edit?
                // lets only pick that single item and display for edit

                const item = items[0];
                setGroupNumber(structuredClone(item));

            } else if (items.length > 1) {

                // multiple items selected, lets create a a new blank template and use that for batch edit
                // later what ever changes that are done to this model we will cascade to selected items.
                const groupNumber = createGroupNumber(props.state.data);
                groupNumber.id = 'bulk';

                setGroupNumber(groupNumber);

            }
        }
    }

    const columns = useMemo(() => {

        const onColumnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
            setGridConfig(t => {
                const gridConfig: ICustomDetailListConfiguration = {
                    sort: [],
                    filter: [],
                    pageSize: 500,
                    page: 1,
                };

                gridConfig.sort = [{
                    column: { key: column.key },
                    isSortedDescending: !t?.sort?.[0]?.isSortedDescending,
                }];

                if (t && t.filter && t.filter.length && t.filter.length > 0) {
                    gridConfig.filter = t.filter;
                }

                if (t?.pageSize)
                    gridConfig.pageSize = t?.pageSize;

                return gridConfig;
            });
        };

        const columns = createGroupStructureColumns(
            props.state.data.config,
            gridConfig,
            onColumnClick,
            undefined,
        )

        return columns;

    },
        [
            onSelect,
            gridConfig,
        ]
    );

    const [items, itemsPaged] = useMemo(() => {
        const items: TGroupNumberGridItem[] = [];

        const gridConfigMappedColumns = {
            filterColumns: gridConfig.filter.map(q => columns?.find(w => w.key === q.column.key)),
            sortColumns: gridConfig.sort.map(q => columns?.find(w => w.key === q.column.key)),
        }

        const toExtract = (item: TGroupNumberGridItem) => () => {
            const groupNumber = item.data!;

            const reportRow: Record<string, string | undefined> = {
                cesNumber: groupNumber.cesNumberRef?.name,
                groupNumber: groupNumber.groupNumber ?? "-",
                planRef: groupNumber.planRef?.name,
                stopLossRef: groupNumber.stopLossRef?.name,
                accountsReceivableRef: groupNumber.accountsReceivableRef?.name,
                status: groupNumber.status?.name,
            }

            groupNumber.fields.forEach((eachField) => {

                const configAttribute = props.state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!];

                // lets always get the first one
                // verified with William there will never be a Tier/PCP/Specialist mode for these.
                eachField.values.forEach(eachFieldValue => {
                    const key = `${configAttribute.bcbs_config_attributeid}-${eachFieldValue?.tierRef?.id ?? ''}`;

                    if (eachFieldValue.value)
                        reportRow[key] = friendlyValue(
                            masterAttribute.bcbs_attributetypecode as EControlType,
                            eachFieldValue.value
                        );
                });
            })

            return reportRow;
        }



        snap.groupStructure?.groupNumbers.forEach((eaGroupNumber, index) => {

            // skip deactivated records/only show deactivated records if permitted at group config properties level
            if (eaGroupNumber.deactivated !== props.state.data.json.showInActives?.value)
                return;

            // Plan view: are we only rows to the matching to current plan?
            if (props.currentPlan)
                if (eaGroupNumber.planRef?.id !== props.currentPlan.id)
                    return;

            // Accounts Receivable view: are we only rows to the matching to current Accounts Receivable?
            if (props.currentAR)
                if (eaGroupNumber.accountsReceivableRef?.id !== props.currentAR.id)
                    return;

            // Stop Loss view: are we only rows to the matching to current Stop Loss?
            if (props.currentStopLoss)
                if (eaGroupNumber.stopLossRef?.id !== props.currentStopLoss.id)
                    return;

            const item: TGroupNumberGridItem = {
                _index: index,
                key: eaGroupNumber.id,
                data: eaGroupNumber,
                cesNumber: eaGroupNumber.cesNumberRef?.name,
                groupNumber: eaGroupNumber.groupNumber,
                planRef: eaGroupNumber?.planRef,
                stopLossRef: eaGroupNumber?.stopLossRef,
                accountsReceivableRef: eaGroupNumber?.accountsReceivableRef,
                status: eaGroupNumber.status?.name,
            };

            eaGroupNumber.fields.forEach(eachField => {
                const configAttributeId = eachField.configRef.id;

                eachField.values.forEach(eachFieldValue => {
                    const key = `${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`
                    const field = eachField.values[0];
                    item[key] = field.value;
                });

            });

            let include = true;
            if (gridConfig && gridConfig.filter && gridConfig.filter.length > 0) {
                gridConfig.filter.forEach((filter, ix) => {
                    const column = gridConfigMappedColumns.filterColumns[ix];
                    if (column) {
                        const predicate = column.filterPredicate;
                        if (predicate)
                            include = include && predicate(item, filter.value, column);
                    }
                });
            }

            item.toExtract = toExtract(item);

            if (include)
                items.push(item);
        });


        if (gridConfig && gridConfig.sort && gridConfig.sort.length > 0)
            items.sort((a, b) => {
                let ret = 0;
                gridConfig.sort?.forEach((sort, ix) => {
                    const column = gridConfigMappedColumns.sortColumns[ix];

                    // TODO why was this needed?
                    if (!column)
                        return;

                    const predicate = column.sortPredicate;
                    const isSortedDescending = sort.isSortedDescending ?? false;

                    if (predicate)
                        ret = isSortedDescending
                            ? predicate(a, b, undefined, column) * -1
                            : predicate(a, b, undefined, column);
                });

                return ret;

            });

        const itemsPaged = items.slice((gridConfig.page - 1) * gridConfig.pageSize, gridConfig.page * gridConfig.pageSize);

        return [items, itemsPaged];

    },
        [
            snap.groupStructure?.groupNumbers,
            props.state.data.json.showInActives?.value,
            gridConfig,
            columns,
        ]
    );

    useEffect(() => {
        if (history.location.search === '?navigation=pending')
            if (params.groupNumberId)
                setTimeout(() => {
                    const groupNumber = snap.groupStructure?.groupNumbers.find(t => t.id === params.groupNumberId);
                    if (groupNumber) {
                        onSelect([groupNumber]);

                        if (!params.field2ndId && !params.tab2ndId && !params.section2ndId)
                            history.push({ pathname: history.location.pathname, search: '?navigation=complete' })
                    }
                }, 200);
    }, []);



    // useEffect(() => {
    //     setTimeout(() => {
    //         const item = items.filter(q => q.key === '00ae3af3-3ed7-4b23-bcec-e0c0a8a913e3')

    //         onSelect(item.map(q => q.data!));
    //     }, 5000);
    // }, [items]);


    const [selectedItems, setSelectedItems] = useState<FAM_IGroupNumber[] | null>(null);
    const selection = useRef(new Selection({
        onSelectionChanged: () => {
            const selectionCount = selection.current.getSelectedCount();
            setSelectionCount(selectionCount);
        },
        selectionMode: SelectionMode.multiple,
    }));


    const menu: IContextualMenuItem[] = [];
    if (!props.disabled && application.ChangeTypePermissions.groupStructure.groupNumber.add)
        menu.push({
            key: `groupNumberPageAdd`,
            name: 'Add',
            icon: 'PageAdd',
            onClick: (event, item) => {
                if (event?.stopPropagation)
                    event.stopPropagation();

                const groupNumber = createGroupNumber(props.state.data);

                if (props.currentPlan)
                    groupNumber.planRef = { id: props.currentPlan.id, name: props.currentPlan.name };

                if (props.currentAR)
                    groupNumber.accountsReceivableRef = { id: props.currentAR.id, name: props.currentAR.name };

                if (props.currentStopLoss)
                    groupNumber.stopLossRef = { id: props.currentStopLoss.id, name: props.currentStopLoss.name };

                emitCustomEvent({
                    eventType: 'NewGroupConfiguration',
                    details: groupNumber
                });

            }
        });

    if (!props.disabled)
        if (selectionCount > 0)
            menu.push({
                key: `groupNumberPageEdit`,
                name: 'Edit',
                icon: 'PageEdit',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();


                    let selectedItems = selection.current.getSelection() as TGroupNumberGridItem[];

                    if (selection.current.isAllSelected())
                        selectedItems = items;

                    onSelect(selectedItems.map(q => q.data!));
                },
            });

    if (props.disabled)
        if (selectionCount === 1)
            menu.push({
                key: `groupNumber.pageView`,
                name: 'View',
                icon: 'Page',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const selectedItems = selection.current.getSelection() as TGroupNumberGridItem[];

                    onSelect(selectedItems.map(q => q.data!));
                },
            });


    return (
        <>
            <GroupStructureGroupNumberEditor
                defaultValue={groupNumber!}
                disabled={props.disabled}
                visibility={props.visibility}
                onChange={(groupNumber) => {

                    if (!groupNumber)
                        return;

                    if (groupNumber.id === 'bulk') {

                        // is this special batch handler, that we have to apply to all selected items?
                        if (!selectedItems)
                            return;

                        const clones = structuredClone(selectedItems);

                        clones.forEach(eClone => {
                            eClone.fields.forEach(eField => {
                                // fields
                                const matchingField = groupNumber.fields.find(q => q.configRef.id === eField.configRef.id);
                                if (matchingField) {
                                    const value = matchingField.values[0].value;
                                    if (value) {
                                        eField.values[0].value = value;
                                    }
                                }
                            })

                            copyPropertiesOnly(groupNumber,
                                eClone,
                                ['planRef', 'accountsReceivableRef', 'stopLossRef', 'cesNumberRef', 'status', 'deactivated'],
                                false,
                                (key, source) => {
                                    if (source[key] === undefined)
                                        return false;

                                    return true;
                                });
                        });

                        dispatch(addUpdateGroupStructure({ groupNumbers: clones }))
                    }
                    else {

                        // we are only modifying single item
                        if (!groupNumber)
                            return;

                        dispatch(addUpdateGroupStructure({ groupNumbers: [groupNumber] }));
                    }

                    setGroupNumber(null);
                }}
                onDismiss={() => setGroupNumber(null)}
                isOpen={!!groupNumber}
            />

            <CustomPanel
                title={`Group Number (${items.length})`}
                id={"groupStructure.groupNumber"}
                storageId={"groupStructure.groupNumber"}
                canCollapse={true}
                defaultCollapsed={params?.groupNumberId ? false : true}
                items={menu}
                mountWhenExpanded={true}
            >
                <CustomDetailList
                    items={items}
                    itemsPaged={itemsPaged}
                    columns={columns}
                    selection={selection.current}
                    // compact={true}
                    defaultGridConfig={gridConfig}
                    quickSearchColumnKey={'groupNumber'}
                    layoutMode={DetailsListLayoutMode.fixedColumns}
                    getKey={(item, index) => { return item.key }}
                    setKey={'key'}
                    gridConfig={[gridConfig, setGridConfig]}
                    onItemInvoked={(item: IGridItem, index, event) => {
                        // to wait for render cycle to kick in first
                        // to open the right item, click was not passing in the right selected item.
                        setTimeout(() => {
                            const selectedItems = selection.current.getSelection() as TGroupNumberGridItem[];
                            onSelect(selectedItems.map(q => q.data!));
                        }, 60);
                    }}
                    onExtract={() => { DetailListExcelGenerationAsync(dispatch, props.state, application, columns, items) }}

                />
            </CustomPanel>
        </>
    )
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupStructureGroupNumberView);
